// ── Interfaces ──

export interface CalculationResult {
  year: number;
  age: number;
  etfValue: number;
  cashValue: number;
  cryptoValue: number;
  realEstateValue: number;
  bondsValue: number;
  totalInvestment: number;
  totalValue: number;
  realValue?: number;
  taxedValue?: number;
}

export interface PredefinedScenario {
  name: string;
  description: string;
  returnRate: number;
  inflationRate: number;
}

export interface Scenario {
  id: string;
  name: string;
  currentAge: number;
  initialInvestment: number;
  monthlyInvestment: number;
  returnRate: number;
  contributionYears: number;
  targetRetirementAge: number;
  desiredMonthlyIncome: number;
  chartData: CalculationResult[];
}

export interface CashConfig {
  startAmount: number;
  annualRate: number;
}

export type CryptoScenario = 'bull' | 'base' | 'bear';

export interface CryptoConfig {
  enabled: boolean;
  btcAmount: number;
  buyPriceEur: number;
  scenario: CryptoScenario;
}

export interface RealEstateConfig {
  enabled: boolean;
  purchasePrice: number;
  annualAppreciation: number;
  isRental: boolean;
  monthlyRent: number;
  monthlyExpenses: number;
  hasLoan: boolean;
  loanAmount: number;
  loanInterestRate: number;
  loanTermYears: number;
}

export interface BondsConfig {
  enabled: boolean;
  startAmount: number;
  couponRate: number;
}

export interface PortfolioParams {
  currentAge: number;
  grossMonthlySalary: number;
  etfStartValue: number;
  monthlyInvestment: number;
  returnRate: number;
  contributionYears: number;
  targetRetirementAge: number;
  desiredMonthlyIncome: number;
  inflationRate: number;
  taxRate: number;
  taxFreibetrag: number;
  cash: CashConfig;
  crypto: CryptoConfig;
  realEstate: RealEstateConfig;
  bonds: BondsConfig;
}

// ── Constants ──

export const MAX_AGE = 85;

export const CRYPTO_SCENARIO_RATES: Record<CryptoScenario, number> = {
  bull: 25,
  base: 10,
  bear: -10,
};

export const ETF_TEILFREISTELLUNG = 0.30;

// Gesetzliche Rente (vereinfacht, Stand 2024)
export const DURCHSCHNITTSENTGELT = 45358;
export const RENTENWERT = 39.32;

export function calculateStatutoryPension(grossMonthlySalary: number, workingYears: number): number {
  if (grossMonthlySalary <= 0 || workingYears <= 0) return 0;
  const grossAnnual = grossMonthlySalary * 12;
  const pointsPerYear = grossAnnual / DURCHSCHNITTSENTGELT;
  const totalPoints = pointsPerYear * workingYears;
  return totalPoints * RENTENWERT;
}

// ── Netto-Berechnung (Steuerklasse 1, keine Kirchensteuer, kinderlos, BaWü) ──

export interface NetSalaryResult {
  grossMonthly: number;
  netMonthly: number;
  incomeTaxMonthly: number;
  soliMonthly: number;
  socialContributionsMonthly: number;
  rvMonthly: number;
  kvMonthly: number;
  pvMonthly: number;
  avMonthly: number;
}

// Sozialversicherungs-Sätze 2024 (Arbeitnehmeranteil)
const RV_RATE = 0.093;
const KV_RATE = 0.073;
const KV_ZUSATZ = 0.0085; // Ø Zusatzbeitrag / 2
const PV_RATE = 0.017;
const PV_KINDERLOS_ZUSCHLAG = 0.006;
const AV_RATE = 0.013;
const RV_BBG_MONTHLY = 7550;  // West
const KV_BBG_MONTHLY = 5175;

function calculateIncomeTax(zvE: number): number {
  if (zvE <= 11604) return 0;
  if (zvE <= 17005) {
    const y = (zvE - 11604) / 10000;
    return Math.floor((922.98 * y + 1400) * y);
  }
  if (zvE <= 66760) {
    const z = (zvE - 17005) / 10000;
    return Math.floor((181.19 * z + 2397) * z + 1025.38);
  }
  if (zvE <= 277825) return Math.floor(0.42 * zvE - 10602.13);
  return Math.floor(0.45 * zvE - 18936.88);
}

function calculateSoli(est: number): number {
  if (est <= 18130) return 0;
  return Math.floor(Math.min(0.055 * est, 0.119 * (est - 18130)) * 100) / 100;
}

export function calculateNetSalary(grossMonthly: number): NetSalaryResult {
  if (grossMonthly <= 0) {
    return { grossMonthly: 0, netMonthly: 0, incomeTaxMonthly: 0, soliMonthly: 0, socialContributionsMonthly: 0, rvMonthly: 0, kvMonthly: 0, pvMonthly: 0, avMonthly: 0 };
  }

  const rvBasis = Math.min(grossMonthly, RV_BBG_MONTHLY);
  const kvBasis = Math.min(grossMonthly, KV_BBG_MONTHLY);

  const rv = rvBasis * RV_RATE;
  const kv = kvBasis * (KV_RATE + KV_ZUSATZ);
  const pv = kvBasis * (PV_RATE + PV_KINDERLOS_ZUSCHLAG);
  const av = rvBasis * AV_RATE;
  const socialTotal = rv + kv + pv + av;

  const grossAnnual = grossMonthly * 12;
  const socialAnnual = socialTotal * 12;

  // Zu versteuerndes Einkommen (vereinfacht)
  const werbungskosten = 1230;
  const sonderausgaben = 36;
  const vorsorgeRV = rv * 12;
  const vorsorgeKVPV = (kv + pv) * 12 * 0.96;
  const zvE = Math.max(0, grossAnnual - werbungskosten - sonderausgaben - vorsorgeRV - vorsorgeKVPV);

  const est = calculateIncomeTax(zvE);
  const soli = calculateSoli(est);

  const totalDeductions = socialAnnual + est + soli;
  const netAnnual = grossAnnual - totalDeductions;

  return {
    grossMonthly,
    netMonthly: netAnnual / 12,
    incomeTaxMonthly: est / 12,
    soliMonthly: soli / 12,
    socialContributionsMonthly: socialTotal,
    rvMonthly: rv,
    kvMonthly: kv,
    pvMonthly: pv,
    avMonthly: av,
  };
}

export const predefinedScenarios: PredefinedScenario[] = [
  { name: "Optimistisch", description: "Hohe Rendite, niedrige Inflation", returnRate: 8.0, inflationRate: 1.5 },
  { name: "Realistisch", description: "Durchschnittliche historische Werte", returnRate: 6.0, inflationRate: 2.0 },
  { name: "Pessimistisch", description: "Niedrige Rendite, hohe Inflation", returnRate: 4.0, inflationRate: 3.0 },
];

// ── Retirement helpers ──

export function canSustainRetirementFrom(
  value: number, startAge: number, withdrawal: number, rate: number, untilAge: number
): boolean {
  if (startAge < 0 || rate < -100 || untilAge <= startAge) return false;
  let v = value;
  for (let age = startAge + 1; age <= untilAge; age++) {
    v = (v - withdrawal) * (1 + rate / 100);
    if (v < 0) return false;
  }
  return true;
}

export function requiredCapitalForRetirement(withdrawal: number, rate: number, years: number): number {
  if (withdrawal < 0 || years <= 0) return 0;
  if (rate <= 0 || rate > 50) return withdrawal * years;
  const r = rate / 100;
  const result = withdrawal * (1 - Math.pow(1 + r, -years)) / r;
  if (!isFinite(result) || result < 0) return withdrawal * years;
  return result;
}

// ── Loan helpers ──

export function calculateMonthlyAnnuity(loanAmount: number, annualRate: number, termYears: number): number {
  if (loanAmount <= 0 || termYears <= 0) return 0;
  if (annualRate <= 0) return loanAmount / (termYears * 12);
  const r = annualRate / 100 / 12;
  const n = termYears * 12;
  return loanAmount * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
}

export function calculateRemainingLoan(loanAmount: number, annualRate: number, termYears: number, yearsElapsed: number): number {
  if (loanAmount <= 0 || yearsElapsed >= termYears) return 0;
  if (annualRate <= 0) return Math.max(0, loanAmount * (1 - yearsElapsed / termYears));
  const r = annualRate / 100 / 12;
  const n = termYears * 12;
  const p = yearsElapsed * 12;
  const remaining = loanAmount * (Math.pow(1 + r, n) - Math.pow(1 + r, p)) / (Math.pow(1 + r, n) - 1);
  return Math.max(0, remaining);
}

// ── Per-asset growth functions ──

export function calculateEtfGrowth(
  etfStartValue: number, monthlyInvestment: number, returnRate: number,
  contributionYears: number, totalYears: number
): number[] {
  const values: number[] = [];
  let current = etfStartValue;
  for (let y = 0; y < totalYears; y++) {
    values.push(current);
    if (y < contributionYears) {
      current = (current + monthlyInvestment * 12) * (1 + returnRate / 100);
    } else {
      current = current * (1 + returnRate / 100);
    }
  }
  return values;
}

export function calculateCashGrowth(startAmount: number, annualRate: number, totalYears: number): number[] {
  const values: number[] = [];
  let current = startAmount;
  for (let y = 0; y < totalYears; y++) {
    values.push(current);
    current = current * (1 + annualRate / 100);
  }
  return values;
}

export function calculateCryptoGrowth(config: CryptoConfig, totalYears: number): number[] {
  if (!config.enabled || config.btcAmount <= 0) return new Array(totalYears).fill(0);
  const rate = CRYPTO_SCENARIO_RATES[config.scenario];
  const startValue = config.btcAmount * config.buyPriceEur;
  const values: number[] = [];
  for (let y = 0; y < totalYears; y++) {
    values.push(Math.max(0, startValue * Math.pow(1 + rate / 100, y)));
  }
  return values;
}

export function calculateRealEstateGrowth(config: RealEstateConfig, totalYears: number): number[] {
  if (!config.enabled || config.purchasePrice <= 0) return new Array(totalYears).fill(0);
  const values: number[] = [];
  let cumulativeRentalIncome = 0;
  const annuity = config.hasLoan ? calculateMonthlyAnnuity(config.loanAmount, config.loanInterestRate, config.loanTermYears) * 12 : 0;

  for (let y = 0; y < totalYears; y++) {
    const propertyValue = config.purchasePrice * Math.pow(1 + config.annualAppreciation / 100, y);
    const remainingLoan = config.hasLoan ? calculateRemainingLoan(config.loanAmount, config.loanInterestRate, config.loanTermYears, y) : 0;

    if (config.isRental) {
      const netMonthly = Math.max(0, config.monthlyRent - config.monthlyExpenses);
      const annualRentalNet = netMonthly * 12 - (config.hasLoan && y < config.loanTermYears ? annuity : 0);
      cumulativeRentalIncome += Math.max(0, annualRentalNet);
    }

    const equity = propertyValue - remainingLoan + cumulativeRentalIncome;
    values.push(Math.max(0, equity));
  }
  return values;
}

export function calculateBondsGrowth(config: BondsConfig, totalYears: number): number[] {
  if (!config.enabled || config.startAmount <= 0) return new Array(totalYears).fill(0);
  const values: number[] = [];
  let current = config.startAmount;
  for (let y = 0; y < totalYears; y++) {
    values.push(current);
    current = current * (1 + config.couponRate / 100);
  }
  return values;
}

// ── Tax calculation per asset ──

export function calculateTaxedPortfolioValue(
  result: CalculationResult,
  prevResult: CalculationResult | undefined,
  params: { taxRate: number; taxFreibetrag: number; monthlyInvestment: number; isContributing: boolean; holdingYears: number }
): number {
  const { taxRate, taxFreibetrag, monthlyInvestment, isContributing, holdingYears } = params;

  if (!prevResult) return result.totalValue;

  // ETF: Teilfreistellung 30%, rest taxed
  const etfGain = result.etfValue - prevResult.etfValue - (isContributing ? monthlyInvestment * 12 : 0);
  const etfTaxableGain = Math.max(0, etfGain) * (1 - ETF_TEILFREISTELLUNG);

  // Cash: full tax on interest
  const cashGain = Math.max(0, result.cashValue - prevResult.cashValue);

  // Crypto: tax-free after 1 year holding
  const cryptoTaxableGain = holdingYears >= 1 ? 0 : Math.max(0, result.cryptoValue - prevResult.cryptoValue);

  // Real estate: tax-free after 10 years (Spekulationsfrist)
  const reTaxableGain = holdingYears >= 10 ? 0 : Math.max(0, result.realEstateValue - prevResult.realEstateValue);

  // Bonds: full tax on coupon
  const bondsGain = Math.max(0, result.bondsValue - prevResult.bondsValue);

  const totalTaxableGain = etfTaxableGain + cashGain + cryptoTaxableGain + reTaxableGain + bondsGain;
  const afterFreibetrag = Math.max(0, totalTaxableGain - taxFreibetrag);
  const tax = afterFreibetrag * (taxRate / 100);

  return result.totalValue - tax;
}

// ── Portfolio aggregation ──

export function calculatePortfolio(params: PortfolioParams): CalculationResult[] {
  const {
    currentAge, grossMonthlySalary, etfStartValue, monthlyInvestment, returnRate,
    contributionYears, targetRetirementAge, desiredMonthlyIncome,
    inflationRate, taxRate, taxFreibetrag, cash, crypto, realEstate, bonds,
  } = params;

  if (targetRetirementAge < currentAge || currentAge < 0 || contributionYears < 0) return [];

  const totalYearsAccum = Math.max(0, targetRetirementAge - currentAge);
  if (totalYearsAccum === 0) return [];

  const etfValues = calculateEtfGrowth(etfStartValue, monthlyInvestment, returnRate, contributionYears, totalYearsAccum);
  const cashValues = calculateCashGrowth(cash.startAmount, cash.annualRate, totalYearsAccum);
  const cryptoValues = calculateCryptoGrowth(crypto, totalYearsAccum);
  const realEstateValues = calculateRealEstateGrowth(realEstate, totalYearsAccum);
  const bondsValues = calculateBondsGrowth(bonds, totalYearsAccum);

  const results: CalculationResult[] = [];
  let totalInvestment = etfStartValue + cash.startAmount
    + (crypto.enabled ? crypto.btcAmount * crypto.buyPriceEur : 0)
    + (realEstate.enabled ? realEstate.purchasePrice : 0)
    + (bonds.enabled ? bonds.startAmount : 0);

  for (let y = 0; y < totalYearsAccum; y++) {
    const age = currentAge + y;
    const etf = etfValues[y];
    const cashVal = cashValues[y];
    const cryptoVal = cryptoValues[y];
    const reVal = realEstateValues[y];
    const bondsVal = bondsValues[y];
    const totalValue = etf + cashVal + cryptoVal + reVal + bondsVal;

    const inflationFactor = Math.pow(1 - inflationRate / 100, y);
    const realValue = totalValue * inflationFactor;

    const result: CalculationResult = {
      year: y, age, etfValue: etf, cashValue: cashVal, cryptoValue: cryptoVal,
      realEstateValue: reVal, bondsValue: bondsVal, totalInvestment, totalValue, realValue,
    };

    result.taxedValue = calculateTaxedPortfolioValue(
      result, y > 0 ? results[y - 1] : undefined,
      { taxRate, taxFreibetrag, monthlyInvestment, isContributing: y < contributionYears, holdingYears: y }
    );

    results.push(result);

    if (y < contributionYears) {
      totalInvestment += monthlyInvestment * 12;
    }
  }

  // ── Realistic retirement phase ──
  const workingYears = targetRetirementAge - currentAge;
  const monthlyPension = calculateStatutoryPension(grossMonthlySalary, workingYears);
  const netDesired = Math.max(0, desiredMonthlyIncome - monthlyPension);
  const annualWithdrawal = netDesired * 12;
  const lastAccum = results[results.length - 1];
  if (!lastAccum) return results;

  let retEtf = lastAccum.etfValue;
  let retCash = lastAccum.cashValue;
  let retCrypto = lastAccum.cryptoValue;
  let retRe = lastAccum.realEstateValue;
  let retBonds = lastAccum.bondsValue;
  const lastInvestment = lastAccum.totalInvestment;
  const cryptoRate = crypto.enabled ? CRYPTO_SCENARIO_RATES[crypto.scenario] : 0;
  const bondsRate = bonds.enabled ? bonds.couponRate : 0;

  let retYear = 0;
  while (results[results.length - 1].age < MAX_AGE) {
    // Rental income reduces withdrawal need
    const rentalIncome = realEstate.enabled && realEstate.isRental
      ? Math.max(0, realEstate.monthlyRent - realEstate.monthlyExpenses) * 12
      : 0;
    const netWithdrawal = Math.max(0, annualWithdrawal - rentalIncome);

    // Draw from cash first, then bonds, then ETF
    let remaining = netWithdrawal;
    if (retCash > 0) {
      const fromCash = Math.min(retCash, remaining);
      retCash -= fromCash;
      remaining -= fromCash;
    }
    if (remaining > 0 && retBonds > 0) {
      const fromBonds = Math.min(retBonds, remaining);
      retBonds -= fromBonds;
      remaining -= fromBonds;
    }
    if (remaining > 0 && retEtf > 0) {
      const fromEtf = Math.min(retEtf, remaining);
      retEtf -= fromEtf;
      remaining -= fromEtf;
    }
    if (remaining > 0 && retCrypto > 0) {
      const fromCrypto = Math.min(retCrypto, remaining);
      retCrypto -= fromCrypto;
      remaining -= fromCrypto;
    }

    // Grow remaining assets
    retEtf = Math.max(0, retEtf * (1 + returnRate / 100));
    retCash = Math.max(0, retCash * (1 + cash.annualRate / 100));
    retCrypto = Math.max(0, retCrypto * (1 + cryptoRate / 100));
    retBonds = Math.max(0, retBonds * (1 + bondsRate / 100));

    // Real estate continues to appreciate
    const yearsFromStart = totalYearsAccum + retYear + 1;
    if (realEstate.enabled) {
      const propValue = realEstate.purchasePrice * Math.pow(1 + realEstate.annualAppreciation / 100, yearsFromStart);
      const remLoan = realEstate.hasLoan ? calculateRemainingLoan(realEstate.loanAmount, realEstate.loanInterestRate, realEstate.loanTermYears, yearsFromStart) : 0;
      retRe = Math.max(0, propValue - remLoan);
    }

    const totalValue = retEtf + retCash + retCrypto + retRe + retBonds;
    const inflationFactor = Math.pow(1 - inflationRate / 100, yearsFromStart);

    results.push({
      year: yearsFromStart,
      age: targetRetirementAge + retYear,
      etfValue: retEtf, cashValue: retCash, cryptoValue: retCrypto,
      realEstateValue: retRe, bondsValue: retBonds,
      totalInvestment: lastInvestment,
      totalValue,
      realValue: totalValue * inflationFactor,
      taxedValue: totalValue,
    });
    retYear++;
  }

  return results;
}

// backward compat wrapper
export interface CalculateWealthParams {
  currentAge: number;
  initialInvestment: number;
  monthlyInvestment: number;
  returnRate: number;
  contributionYears: number;
  targetRetirementAge: number;
  desiredMonthlyIncome: number;
  inflationRate: number;
  taxRate: number;
  taxFreibetrag: number;
}

const DISABLED_RE: RealEstateConfig = { enabled: false, purchasePrice: 0, annualAppreciation: 0, isRental: false, monthlyRent: 0, monthlyExpenses: 0, hasLoan: false, loanAmount: 0, loanInterestRate: 0, loanTermYears: 0 };
const DISABLED_CRYPTO: CryptoConfig = { enabled: false, btcAmount: 0, buyPriceEur: 0, scenario: 'base' };
const DISABLED_BONDS: BondsConfig = { enabled: false, startAmount: 0, couponRate: 0 };

export function calculateWealth(params: CalculateWealthParams): CalculationResult[] {
  return calculatePortfolio({
    ...params,
    grossMonthlySalary: 0,
    etfStartValue: params.initialInvestment,
    cash: { startAmount: 0, annualRate: 0 },
    crypto: DISABLED_CRYPTO,
    realEstate: DISABLED_RE,
    bonds: DISABLED_BONDS,
  });
}

// ── Finders ──

export function findEarliestRetirementAge(
  chartData: CalculationResult[], currentAge: number, desiredMonthlyIncome: number, returnRate: number
): number | undefined {
  const annualWithdrawal = desiredMonthlyIncome * 12;
  for (let age = Math.max(currentAge, 30); age <= MAX_AGE; age++) {
    const d = chartData.find((d) => d.age === age);
    if (d && canSustainRetirementFrom(d.totalValue, age, annualWithdrawal, returnRate, MAX_AGE)) return age;
  }
  return undefined;
}

export function findIntersection(
  chartData: CalculationResult[], currentAge: number, desiredMonthlyIncome: number, returnRate: number
): { age: number; totalValue: number } | undefined {
  const annualWithdrawal = desiredMonthlyIncome * 12;
  for (let age = currentAge; age <= MAX_AGE; age++) {
    const d = chartData.find((d) => d.age === age);
    if (d && canSustainRetirementFrom(d.totalValue, age, annualWithdrawal, returnRate, MAX_AGE)) {
      return { age: d.age, totalValue: d.totalValue };
    }
  }
  return undefined;
}

export function computeTargetCapital(
  intersection: { age: number; totalValue: number } | undefined,
  targetRetirementAge: number, desiredMonthlyIncome: number, returnRate: number
): number {
  if (intersection) return intersection.totalValue;
  const years = Math.max(1, MAX_AGE - targetRetirementAge);
  const value = requiredCapitalForRetirement(desiredMonthlyIncome * 12, returnRate, years);
  if (!value || value < 0 || !isFinite(value)) return 300_000;
  return value;
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
  }).format(value);
}

// ── localStorage helpers ──

const SCENARIOS_KEY = 'etf-profit-scenarios';

export function loadScenariosFromStorage(): Scenario[] {
  try {
    const raw = localStorage.getItem(SCENARIOS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function saveScenariosToStorage(scenarios: Scenario[]): void {
  try {
    localStorage.setItem(SCENARIOS_KEY, JSON.stringify(scenarios));
  } catch { /* quota exceeded etc. */ }
}
